// Skeleton Loading Cards for better perceived performance
import React from "react";

export const SkeletonStudentCard = () => (
  <div className="border border-pale-dawn rounded-lg overflow-hidden bg-white animate-pulse">
    {/* Student Info */}
    <div className="p-4 bg-gradient-to-r from-ice-white to-pale-dawn/40">
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0 flex-1">
          <div className="h-4 bg-slate-cloud/20 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-slate-cloud/20 rounded w-1/2"></div>
        </div>
        <div className="ml-3 flex-shrink-0">
          <div className="w-10 h-10 bg-slate-cloud/20 rounded-full"></div>
        </div>
      </div>

      {/* Action Buttons Skeleton */}
      <div className="flex gap-2">
        <div className="flex-1 h-8 bg-slate-cloud/20 rounded-lg"></div>
        <div className="flex-1 h-8 bg-slate-cloud/20 rounded-lg"></div>
      </div>
    </div>
  </div>
);

export const SkeletonStudentGrid = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
    {[...Array(count)].map((_, i) => (
      <SkeletonStudentCard key={i} />
    ))}
  </div>
);

export const SkeletonDetailHeader = () => (
  <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30 animate-pulse">
    <div className="flex items-center">
      <div className="w-16 h-16 bg-gray-300 rounded-full mr-6"></div>
      <div className="flex-1">
        <div className="h-8 bg-gray-300 rounded w-48 mb-2"></div>
        <div className="h-5 bg-gray-200 rounded w-32 mb-1"></div>
        <div className="h-4 bg-gray-200 rounded w-40"></div>
      </div>
      <div className="h-8 bg-gray-300 rounded-full w-32"></div>
    </div>
  </div>
);

export const SkeletonGradeChart = () => (
  <div className="card-brand-elevated animate-pulse">
    <div className="bg-gradient-to-r from-grey-dusk to-slate-cloud p-4 rounded-t-lg">
      <div className="h-6 bg-white/20 rounded w-64 mb-2"></div>
      <div className="h-4 bg-white/10 rounded w-48"></div>
    </div>
    <div className="p-6">
      <div className="h-64 bg-gray-100 rounded"></div>
    </div>
  </div>
);

export const SkeletonSchoolCard = () => (
  <div className="card-brand animate-pulse">
    <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 p-6 rounded-t-lg">
      <div className="h-6 bg-white/20 rounded w-48 mb-2"></div>
      <div className="h-4 bg-white/10 rounded w-64"></div>
    </div>
    <div className="p-6">
      <div className="space-y-4">
        <div className="h-48 bg-gray-100 rounded"></div>
        <div className="h-32 bg-gray-50 rounded"></div>
      </div>
    </div>
  </div>
);

export const SkeletonStudentDetailPage = () => (
  <div className="min-h-screen bg-green-cloud">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button Skeleton */}
      <div className="mb-8">
        <div className="h-8 bg-slate-cloud/20 rounded w-32 mb-4 animate-pulse"></div>
        
        {/* Header Skeleton */}
        <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30 animate-pulse">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-slate-cloud/20 rounded-full mr-6"></div>
              <div>
                <div className="h-8 bg-slate-cloud/20 rounded w-48 mb-2"></div>
                <div className="h-5 bg-slate-cloud/20 rounded w-32 mb-1"></div>
                <div className="h-4 bg-slate-cloud/20 rounded w-40"></div>
              </div>
            </div>
            <div className="mt-4 lg:mt-0">
              <div className="h-8 bg-slate-cloud/20 rounded-full w-32"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Overall Chart Skeleton */}
      <div className="mb-8 animate-pulse">
        <div className="card-brand-elevated mb-2 bg-white">
          <div className="bg-gradient-to-r from-grey-dusk to-slate-cloud p-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-6 bg-white/20 rounded w-64 mb-2"></div>
                <div className="h-4 bg-white/10 rounded w-96"></div>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg"></div>
            </div>
          </div>
        </div>
        <div className="card-brand-elevated p-6">
          <div className="h-80 bg-slate-cloud/10 rounded"></div>
        </div>
      </div>

      {/* School Cards Skeleton */}
      <div className="space-y-8">
        {[1, 2].map((i) => (
          <div key={i} className="card-brand animate-pulse">
            <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-6 bg-white/20 rounded w-48 mb-2"></div>
                  <div className="h-4 bg-white/10 rounded w-64"></div>
                </div>
                <div className="flex items-center space-x-4">
                  <div>
                    <div className="h-4 bg-white/10 rounded w-24 mb-1"></div>
                    <div className="h-8 bg-white/20 rounded w-16"></div>
                  </div>
                  <div className="w-12 h-12 bg-white/20 rounded-lg"></div>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {/* Chart Skeleton */}
                <div className="h-64 bg-slate-cloud/10 rounded"></div>
                {/* Table Skeleton */}
                <div className="space-y-3">
                  <div className="h-12 bg-slate-cloud/10 rounded"></div>
                  <div className="h-10 bg-slate-cloud/5 rounded"></div>
                  <div className="h-10 bg-slate-cloud/5 rounded"></div>
                  <div className="h-10 bg-slate-cloud/5 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default SkeletonStudentCard;
